import { action, computed, makeObservable, observable, runInAction, toJS } from 'mobx';

import { Result, RootStore } from '../../../services/rootStore';
import { SortDirection } from '../../../types/commonTypes';
import { getSortFunction } from '../../../utils';

export type GlossaryWord = {
  id: string;
  word: string;
  text: string;
  link: string;
  labelLink: string;
  imageUrl: string;
  tenantId: string;
};

export type GlossaryWordListItem = GlossaryWord;

export type CreateGlossaryWordRequest = {
  id?: string;
  word: string;
  text: string;
  link: string;
  labelLink: string;
  imageUrl: string;
};

export type UpdateGlossaryWordRequest = {
  id: string;
  word: string;
  text: string;
  link: string;
  labelLink: string;
  imageUrl: string;
};

type GlossaryFilter = {
  name: string;
};

export class GlossaryStore {
  rootStore: RootStore;
  words: GlossaryWordListItem[] = [];
  filter: GlossaryFilter = { name: '' };
  isLoading: boolean = false;
  sortOrder: SortDirection = SortDirection.Asc;

  constructor(_rootStore: RootStore) {
    this.rootStore = _rootStore;
    makeObservable(this, {
      words: observable,
      filter: observable,
      isLoading: observable,
      sortOrder: observable,
      filteredWords: computed,
      getAll: action,
      clearWords: action,
      setFilter: action,
      sortWords: action,
    });
  }

  get filteredWords(): GlossaryWordListItem[] {
    const { name } = this.filter;
    if (!name) return this.words;
    const lower = name.toLowerCase();
    return this.words.filter(
      (w) =>
        w.word?.toLowerCase().includes(lower) ||
        w.text?.toLowerCase().includes(lower) ||
        w.link?.toLowerCase().includes(lower) ||
        w.labelLink?.toLowerCase().includes(lower)
    );
  }

  clearWords() {
    this.words = [];
    this.filter = { name: '' };
  }

  setFilter(newFilter: GlossaryFilter) {
    this.filter = { ...newFilter };
  }

  public sortWords(order: SortDirection, field: string) {
    const sortFunc = getSortFunction(field, order);
    const words = toJS(this.words);
    this.words = words.sort(sortFunc);
    this.sortOrder = order;
  }

  public async getAll(): Promise<Result<GlossaryWord[]>> {
    runInAction(() => {
      this.isLoading = true;
    });

    const resp = await this.rootStore.ApiClient.get<Result<GlossaryWord[]>>(
      '/glossary/GetGlossaryWords'
    );

    runInAction(() => {
      this.isLoading = false;
      if (resp.succeeded) {
        this.words = resp.data || [];
      }
    });

    return resp;
  }

  public async create(request: CreateGlossaryWordRequest): Promise<Result<string>> {
    const resp = await this.rootStore.ApiClient.post<
      CreateGlossaryWordRequest,
      Result<string>
    >('/glossary/CreateGlossaryWord', request);

    if (resp.succeeded) {
      await this.getAll();
    }

    return resp;
  }

  public async update(request: UpdateGlossaryWordRequest): Promise<Result<string>> {
    const resp = await this.rootStore.ApiClient.put<
      UpdateGlossaryWordRequest,
      Result<string>
    >('/glossary/UpdateGlossaryWord', request);

    if (resp.succeeded) {
      await this.getAll();
    }

    return resp;
  }

  public async uploadImage(glossaryId: string, file: File): Promise<Result<string>> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('glossaryId', glossaryId);
    return this.rootStore.ApiClient.postForm<Result<string>>(
      '/glossary/UploadGlossaryImage',
      formData
    );
  }

  public async delete(id: string): Promise<Result<string>> {
    const resp = await this.rootStore.ApiClient.delete(
      `/glossary/DeleteGlossaryWord/${id}`
    );

    if (resp.succeeded) {
      runInAction(() => {
        this.words = this.words.filter((w) => w.id !== id);
      });
    }

    return resp;
  }
}
